import {
  clearDriverRouteDraft,
  discardPendingDriverRoute,
  deleteDriverCache,
  getDriverCache,
  getDriverRouteDraft,
  putDriverCache,
  resolveDriverMediaReferences,
  queueDriverAction,
  saveDriverRouteDraft,
} from "../driver/offline-store.js";

const RETRYABLE_GET_STATUSES = new Set([429, 502, 503, 504]);
const DRIVER_CACHED_GET_TIMEOUT_MS = 8_000;

function wait(milliseconds) {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

async function apiRequest(path, options = {}) {
  const {
    timeoutMs = null,
    maxAttempts: requestedMaxAttempts = null,
    ...fetchOptions
  } = options;
  const method = String(fetchOptions.method || "GET").toUpperCase();
  const requestOptions = {
    ...fetchOptions,
    headers: {
      "content-type": "application/json",
      ...fetchOptions.headers,
    },
  };

  let response = null;
  let networkError = null;
  const maximumAttempts =
    requestedMaxAttempts ?? (method === "GET" ? 2 : 1);

  for (let attempt = 1; attempt <= maximumAttempts; attempt += 1) {
    const controller = timeoutMs ? new AbortController() : null;
    const timer = controller
      ? window.setTimeout(
          () =>
            controller.abort(
              new DOMException("Request timeout", "TimeoutError"),
            ),
          timeoutMs,
        )
      : null;

    try {
      response = await fetch(
        path,
        controller ? { ...requestOptions, signal: controller.signal } : requestOptions,
      );
      networkError = null;
    } catch (error) {
      networkError = error;
    } finally {
      if (timer) window.clearTimeout(timer);
    }

    const shouldRetry =
      attempt < maximumAttempts &&
      (networkError || RETRYABLE_GET_STATUSES.has(response?.status));

    if (!shouldRetry) break;
    await wait(250 * attempt);
  }

  if (networkError || !response) {
    const error = new Error(
      "Koneksi ke server Route Planning terputus. Silakan coba kembali.",
    );
    error.code = "NETWORK_ERROR";
    error.status = 0;
    throw error;
  }

  const payload = await response
    .json()
    .catch(() => ({}));

  if (!response.ok) {
    const correlationId =
      payload.correlationId ||
      response.headers.get("x-correlation-id") ||
      response.headers.get("cf-ray");

    const fallbackMessage = RETRYABLE_GET_STATUSES.has(response.status)
      ? "Server Route Planning sedang sibuk. Data belum dapat dimuat."
      : "Permintaan ke server Route Planning gagal.";

    const error = new Error(payload.message || fallbackMessage);
    error.code = payload.code || "API_ERROR";
    error.status = response.status;
    error.details = payload.details;
    error.correlationId = correlationId;
    throw error;
  }

  return payload;
}

function withQuery(path, params = {}) {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(
    ([key, value]) => {
      if (
        value !== undefined &&
        value !== null &&
        value !== ""
      ) {
        searchParams.set(key, String(value));
      }
    },
  );

  const query = searchParams.toString();

  return `${path}${query ? `?${query}` : ""}`;
}

export function getPendingOrders(params = {}) {
  return apiRequest(
    withQuery("/api/planning/orders", params),
  );
}

export function getPendingDocuments(params = {}) {
  return apiRequest(
    withQuery("/api/orders/pending", params),
  );
}

export function getProcessedOrders(params = {}) {
  return apiRequest(
    withQuery("/api/orders/processed", params),
  );
}

export function triggerProcessedOrderSync(payload = {}) {
  return apiRequest("/api/orders/processed/sync", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function getOrderDocumentDetail(type, id, options = {}) {
  return apiRequest(
    withQuery(
      `/api/orders/${encodeURIComponent(type)}/${encodeURIComponent(id)}`,
      {
        force: options.force ? 1 : "",
      },
    ),
  );
}

export function saveOrderDocumentShippingAddress(
  type,
  id,
  customerShippingAddressId,
) {
  return apiRequest(
    `/api/orders/${encodeURIComponent(type)}/${encodeURIComponent(id)}/shipping-address`,
    {
      method: "PUT",
      body: JSON.stringify({ customerShippingAddressId }),
    },
  );
}

export function clearOrderDocumentShippingAddress(type, id) {
  return apiRequest(
    `/api/orders/${encodeURIComponent(type)}/${encodeURIComponent(id)}/shipping-address`,
    {
      method: "DELETE",
    },
  );
}

export function getPendingOrderDetail(
  id,
  options = {},
) {
  return apiRequest(
    withQuery(
      `/api/planning/orders/${encodeURIComponent(id)}/detail`,
      {
        force: options.force ? 1 : "",
      },
    ),
  );
}

export function savePendingOrderShippingAddress(
  id,
  customerShippingAddressId,
) {
  return apiRequest(
    `/api/planning/orders/${encodeURIComponent(id)}/shipping-address`,
    {
      method: "PUT",
      body: JSON.stringify({
        customerShippingAddressId,
      }),
    },
  );
}

export function clearPendingOrderShippingAddress(id) {
  return apiRequest(
    `/api/planning/orders/${encodeURIComponent(id)}/shipping-address`,
    {
      method: "DELETE",
    },
  );
}

export function triggerManualOrderSync() {
  return apiRequest("/api/sync/accurate/orders/manual", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function triggerOrderControlSync() {
  return apiRequest("/api/orders/sync", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function previewOrderReconciliation(salesOrderIds) {
  return apiRequest("/api/orders/reconciliation/preview", {
    method: "POST",
    body: JSON.stringify({ salesOrderIds }),
  });
}

export function getSyncRun(id) {
  return apiRequest(`/api/sync/runs/${encodeURIComponent(id)}`);
}

export function getSyncRuns(limit = 20) {
  return apiRequest(`/api/sync/runs?limit=${limit}`);
}

export function getItemSyncErrors(id, params = {}) {
  return apiRequest(
    withQuery(`/api/sync/runs/${encodeURIComponent(id)}/errors`, params),
  );
}

export function getItemSyncReconciliation(params = {}) {
  return apiRequest(
    withQuery("/api/sync/accurate/items/reconciliation", params),
  );
}

export function importMasterData(payload) {
  return apiRequest("/api/master-data/import", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function enrichOrderRoutes() {
  return apiRequest("/api/enrichment/orders/routes", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function triggerCustomerSync() {
  return apiRequest("/api/sync/accurate/customers/manual", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function triggerStockSync() {
  return apiRequest("/api/sync/accurate/stock/manual", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function triggerDoStockSync(fleetOrderId) {
  return apiRequest(
    `/api/do-preparations/${encodeURIComponent(fleetOrderId)}/stock-sync`,
    {
      method: "POST",
      body: JSON.stringify({}),
    },
  );
}

export function triggerItemMasterSync() {
  return apiRequest("/api/sync/accurate/items/manual", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function testItemMasterSync() {
  return apiRequest("/api/sync/accurate/items/test", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function getCustomers(params = {}) {
  return apiRequest(withQuery("/api/master-data/customers", params));
}

export function updateCustomerCoordinates(id, payload) {
  return apiRequest(
    `/api/master-data/customers/${encodeURIComponent(id)}/coordinates`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    },
  );
}

export function restoreCustomerAccurateCoordinates(id) {
  return apiRequest(
    `/api/master-data/customers/${encodeURIComponent(id)}/coordinates/restore-accurate`,
    {
      method: "POST",
      body: JSON.stringify({}),
    },
  );
}

export function getDepots(params = {}) {
  return apiRequest(withQuery("/api/master-data/depots", params));
}

export function createDepot(payload) {
  return apiRequest("/api/master-data/depots", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateDepot(id, payload) {
  return apiRequest(
    `/api/master-data/depots/${encodeURIComponent(id)}`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    },
  );
}

export function deleteDepot(id) {
  return apiRequest(
    `/api/master-data/depots/${encodeURIComponent(id)}`,
    {
      method: "DELETE",
    },
  );
}

export function getMapConfig() {
  return apiRequest("/api/map/config");
}

export function getMapPois(params = {}) {
  return apiRequest(withQuery("/api/master-data/pois", params));
}

export function createMapPoi(payload) {
  return apiRequest("/api/master-data/pois", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateMapPoi(id, payload) {
  return apiRequest(
    `/api/master-data/pois/${encodeURIComponent(id)}`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    },
  );
}

export function deleteMapPoi(id) {
  return apiRequest(
    `/api/master-data/pois/${encodeURIComponent(id)}`,
    {
      method: "DELETE",
    },
  );
}

export function getStock(params = {}) {
  return apiRequest(withQuery("/api/master-data/stock", params));
}

export function getWarehouses() {
  return apiRequest("/api/master-data/warehouses");
}

export function getStockItemOptions(params = {}) {
  return apiRequest(withQuery("/api/master-data/stock-item-options", params));
}

export function getSkuLogistics(params = {}) {
  return apiRequest(withQuery("/api/master-data/sku-logistics", params));
}

export function getSkuUomMappings(params = {}) {
  return apiRequest(withQuery("/api/master-data/sku-uom-mappings", params));
}

export function getRoutes() {
  return apiRequest("/api/master-data/routes");
}

export function createRoute(payload) {
  return apiRequest("/api/master-data/routes", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateRoute(id, payload) {
  return apiRequest(`/api/master-data/routes/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function deleteRoute(id) {
  return apiRequest(`/api/master-data/routes/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function getRouteAreaMappings() {
  return apiRequest("/api/master-data/route-area-mappings");
}

export function createRouteAreaMapping(payload) {
  return apiRequest("/api/master-data/route-area-mappings", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateRouteAreaMapping(id, payload) {
  return apiRequest(
    `/api/master-data/route-area-mappings/${encodeURIComponent(id)}`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    },
  );
}

export function deleteRouteAreaMapping(id) {
  return apiRequest(
    `/api/master-data/route-area-mappings/${encodeURIComponent(id)}`,
    { method: "DELETE" },
  );
}

export function getAreaOverrides() {
  return apiRequest("/api/master-data/area-overrides");
}

export function createAreaOverride(payload) {
  return apiRequest("/api/master-data/area-overrides", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateAreaOverride(id, payload) {
  return apiRequest(
    `/api/master-data/area-overrides/${encodeURIComponent(id)}`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    },
  );
}

export function deleteAreaOverride(id) {
  return apiRequest(
    `/api/master-data/area-overrides/${encodeURIComponent(id)}`,
    { method: "DELETE" },
  );
}

export function getCustomerShippingAddresses(params = {}) {
  return apiRequest(withQuery("/api/master-data/customer-shipping-addresses", params));
}

export function createCustomerShippingAddress(payload) {
  return apiRequest("/api/master-data/customer-shipping-addresses", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateCustomerShippingAddress(id, payload) {
  return apiRequest(
    `/api/master-data/customer-shipping-addresses/${encodeURIComponent(id)}`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    },
  );
}

export function deleteCustomerShippingAddress(id) {
  return apiRequest(
    `/api/master-data/customer-shipping-addresses/${encodeURIComponent(id)}`,
    { method: "DELETE" },
  );
}

export function getFleetOrders(params = {}) {
  return apiRequest(withQuery("/api/fleet-orders", params));
}

export function getFleetOrder(id) {
  return apiRequest(`/api/fleet-orders/${encodeURIComponent(id)}`);
}

export function getFleetOrderMap(id) {
  return apiRequest(`/api/fleet-orders/${encodeURIComponent(id)}/map`);
}

export function calculateFleetOrderRoute(id, options = {}) {
  return apiRequest(
    withQuery(
      `/api/fleet-orders/${encodeURIComponent(id)}/route/calculate`,
      {
        force: options.force ? "1" : "",
      },
    ),
    {
      method: "POST",
      body: JSON.stringify({
        force: options.force === true,
      }),
    },
  );
}

export function optimizeFleetOrderRoute(id) {
  return apiRequest(
    `/api/fleet-orders/${encodeURIComponent(id)}/route/optimize`,
    {
      method: "POST",
      body: JSON.stringify({}),
    },
  );
}

export function previewFleetOrderRoute(id, orderedStopKeys) {
  return apiRequest(
    `/api/fleet-orders/${encodeURIComponent(id)}/route/preview`,
    {
      method: "POST",
      body: JSON.stringify({
        orderedStopKeys,
      }),
    },
  );
}

export function getNextFleetOrderNumber(foDate) {
  return apiRequest(
    withQuery("/api/fleet-orders/next-number", { foDate }),
  );
}

export function createFleetOrder(payload) {
  return apiRequest("/api/fleet-orders", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function prepareFleetDocuments(documents) {
  return apiRequest("/api/fleet-orders/prepare", {
    method: "POST",
    body: JSON.stringify({ documents }),
  });
}

export function prepareFleetOrders(salesOrderIds) {
  return prepareFleetDocuments(
    (salesOrderIds || []).map((documentId) => ({
      documentType: "SALES_ORDER",
      documentId,
    })),
  );
}

export function addDocumentsToFleetOrder(id, documents) {
  return apiRequest(
    `/api/fleet-orders/${encodeURIComponent(id)}/orders`,
    {
      method: "POST",
      body: JSON.stringify({ documents }),
    },
  );
}

export function addOrdersToFleetOrder(id, salesOrderIds) {
  return addDocumentsToFleetOrder(
    id,
    (salesOrderIds || []).map((documentId) => ({
      documentType: "SALES_ORDER",
      documentId,
    })),
  );
}

export function getFleetOrderOperationalOverrideWorkspace(id, params = {}) {
  return apiRequest(
    withQuery(
      `/api/fleet-orders/${encodeURIComponent(id)}/operational-override`,
      params,
    ),
  );
}

export function applyFleetOrderOperationalOverride(id, payload) {
  return apiRequest(
    `/api/fleet-orders/${encodeURIComponent(id)}/operational-override`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}

export function getFleetOrderCorrectionWorkspace(id) {
  return apiRequest(
    `/api/fleet-orders/${encodeURIComponent(id)}/operational-corrections`,
  );
}

export function applyFleetOrderOperationalCorrection(id, payload) {
  return apiRequest(
    `/api/fleet-orders/${encodeURIComponent(id)}/operational-corrections`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}

export function getFleetOrderAmendmentWorkspace(id, params = {}) {
  return apiRequest(
    withQuery(
      `/api/fleet-orders/${encodeURIComponent(id)}/emergency-amendment`,
      params,
    ),
  );
}

export function previewFleetOrderEmergencyAmendment(id, payload) {
  return apiRequest(
    `/api/fleet-orders/${encodeURIComponent(id)}/emergency-amendment/preview`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}

export function applyFleetOrderEmergencyAmendment(id, payload) {
  return apiRequest(
    `/api/fleet-orders/${encodeURIComponent(id)}/emergency-amendment`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}

export function removeDocumentFromFleetOrder(
  fleetOrderId,
  documentType,
  documentId,
) {
  return apiRequest(
    `/api/fleet-orders/${encodeURIComponent(fleetOrderId)}/documents/${encodeURIComponent(documentType)}/${encodeURIComponent(documentId)}`,
    { method: "DELETE" },
  );
}

export function removeOrderFromFleetOrder(fleetOrderId, salesOrderId) {
  return removeDocumentFromFleetOrder(
    fleetOrderId,
    "SALES_ORDER",
    salesOrderId,
  );
}

export function updateFleetOrder(id, payload) {
  return apiRequest(`/api/fleet-orders/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}


export function completeFleetOrderManually(
  id,
  {
    reason,
    confirmManualCompletion = true,
  } = {},
) {
  return updateFleetOrder(id, {
    status: "COMPLETED",
    manualCompletion: true,
    manualCompletionReason: reason,
    confirmManualCompletion,
  });
}

export function saveFleetOrderStopSequence(id, stops) {
  return apiRequest(
    `/api/fleet-orders/${encodeURIComponent(id)}/stop-sequence`,
    {
      method: "PUT",
      body: JSON.stringify({
        stops,
      }),
    },
  );
}

export function resetFleetOrderStopSequence(id) {
  return apiRequest(
    `/api/fleet-orders/${encodeURIComponent(id)}/stop-sequence`,
    {
      method: "DELETE",
    },
  );
}

export function cancelFleetOrder(id) {
  return apiRequest(
    `/api/fleet-orders/${encodeURIComponent(id)}/cancel`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export function deleteFleetOrder(id, deletionReason) {
  return apiRequest(
    `/api/fleet-orders/${encodeURIComponent(id)}/delete`,
    {
      method: "POST",
      body: JSON.stringify({ deletionReason }),
    },
  );
}

export function checkFleetOrderChanges(id) {
  return apiRequest(
    `/api/fleet-orders/${encodeURIComponent(id)}/check-changes`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export function updateFleetOrderSnapshot(id) {
  return apiRequest(
    `/api/fleet-orders/${encodeURIComponent(id)}/update-snapshot`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export function getDoPreparationFleetOrders(params = {}) {
  return apiRequest(withQuery("/api/do-preparations", params));
}

export function getDoPreparationDetail(fleetOrderId) {
  return apiRequest(
    `/api/do-preparations/${encodeURIComponent(fleetOrderId)}`,
  );
}

export function getDoWriteContract(fleetOrderId) {
  return apiRequest(
    `/api/do-preparations/${encodeURIComponent(fleetOrderId)}/write-contract`,
  );
}

export function getDoBatchPlan(fleetOrderId) {
  return apiRequest(
    `/api/do-preparations/${encodeURIComponent(fleetOrderId)}/batch-plan`,
  );
}

export function createDoBatchPlan(fleetOrderId) {
  return apiRequest(
    `/api/do-preparations/${encodeURIComponent(fleetOrderId)}/batch-plan`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export function updateDoBatchPlanPriority(
  fleetOrderId,
  sourceKeys,
  partialSourceKeys = [],
  sourceItemSelections = [],
) {
  return apiRequest(
    `/api/do-preparations/${encodeURIComponent(fleetOrderId)}/batch-plan/priority`,
    {
      method: "PATCH",
      body: JSON.stringify({
        sourceKeys,
        partialSourceKeys,
        sourceItemSelections,
      }),
    },
  );
}

export function getDoBatchExecution(fleetOrderId) {
  return apiRequest(
    `/api/do-preparations/${encodeURIComponent(fleetOrderId)}/batch-execution`,
  );
}

export function startDoBatchExecution(fleetOrderId) {
  return apiRequest(
    `/api/do-preparations/${encodeURIComponent(fleetOrderId)}/batch-execution`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export function pauseDoBatchExecution(fleetOrderId) {
  return apiRequest(
    `/api/do-preparations/${encodeURIComponent(fleetOrderId)}/batch-execution/pause`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export function resumeDoBatchExecution(fleetOrderId) {
  return apiRequest(
    `/api/do-preparations/${encodeURIComponent(fleetOrderId)}/batch-execution/resume`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export function terminateDoBatchExecution(fleetOrderId) {
  return apiRequest(
    `/api/do-preparations/${encodeURIComponent(fleetOrderId)}/batch-execution/terminate`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export function getDoWriteProfileStatus() {
  return apiRequest("/api/do-preparations/write-profile-status");
}

function getDoSourceWritePath(
  fleetOrderId,
  sourceType,
  sourceDocumentId,
  action,
) {
  return `/api/do-preparations/${encodeURIComponent(
    fleetOrderId,
  )}/sources/${encodeURIComponent(sourceType)}/${encodeURIComponent(
    sourceDocumentId,
  )}/${action}`;
}

export function getDoSourceWriteStatus(
  fleetOrderId,
  sourceType,
  sourceDocumentId,
) {
  return apiRequest(
    getDoSourceWritePath(
      fleetOrderId,
      sourceType,
      sourceDocumentId,
      "write-status",
    ),
  );
}

export function getDoSourceWriteHistory(
  fleetOrderId,
  sourceType,
  sourceDocumentId,
) {
  return apiRequest(
    getDoSourceWritePath(
      fleetOrderId,
      sourceType,
      sourceDocumentId,
      "write-history",
    ),
  );
}

export function createDoFromSource(
  fleetOrderId,
  sourceType,
  sourceDocumentId,
  expectedPayloadHash,
  {
    descriptionMode = "SOURCE",
    description = "",
    deliveryMode = null,
    itemSelections = [],
  } = {},
) {
  return apiRequest(
    getDoSourceWritePath(
      fleetOrderId,
      sourceType,
      sourceDocumentId,
      "create",
    ),
    {
      method: "POST",
      body: JSON.stringify({
        expectedPayloadHash,
        descriptionMode,
        description,
        deliveryMode,
        itemSelections,
      }),
    },
  );
}

export function reconcileDoSourceWrite(
  fleetOrderId,
  sourceType,
  sourceDocumentId,
) {
  return apiRequest(
    getDoSourceWritePath(
      fleetOrderId,
      sourceType,
      sourceDocumentId,
      "reconcile",
    ),
    { method: "POST", body: JSON.stringify({}) },
  );
}

export function updateDoDepotWarehouseMapping(fleetOrderId, warehouseId) {
  return apiRequest(
    `/api/do-preparations/${encodeURIComponent(fleetOrderId)}/depot-warehouse-mapping`,
    {
      method: "PATCH",
      body: JSON.stringify({ warehouseId }),
    },
  );
}

export function syncDoPreparationItems(fleetOrderId) {
  return apiRequest(
    `/api/do-preparations/${encodeURIComponent(fleetOrderId)}/sync-items`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export function checkDoAccurate(fleetOrderId) {
  return apiRequest(
    `/api/do-preparations/${encodeURIComponent(fleetOrderId)}/check-do-accurate`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export function startDoReconciliation(
  fleetOrderId,
  { force = false, fullRecheck = false, freshnessMinutes = 5 } = {},
) {
  return apiRequest(
    `/api/do-preparations/${encodeURIComponent(fleetOrderId)}/reconciliation`,
    {
      method: "POST",
      body: JSON.stringify({ force, fullRecheck, freshnessMinutes }),
    },
  );
}

export function startDoReconciliationBatch(
  fleetOrderIds,
  { force = false, fullRecheck = false, freshnessMinutes = 5 } = {},
) {
  return apiRequest("/api/do-preparations/reconcile", {
    method: "POST",
    body: JSON.stringify({
      fleetOrderIds,
      force,
      fullRecheck,
      freshnessMinutes,
    }),
  });
}

export function getDoReconciliationStatus(fleetOrderId) {
  return apiRequest(
    `/api/do-preparations/${encodeURIComponent(fleetOrderId)}/reconciliation`,
  );
}

export function recalculateDoStock(fleetOrderId) {
  return apiRequest(
    `/api/do-preparations/${encodeURIComponent(fleetOrderId)}/recalculate-stock`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export function refreshDoPreparationLiveStock(preparationId) {
  return apiRequest(
    `/api/do-preparations/${encodeURIComponent(preparationId)}/live-stock`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export function updateDoPreparationDefaultWarehouse(
  preparationId,
  warehouseId,
) {
  return apiRequest(
    `/api/do-preparations/${encodeURIComponent(preparationId)}/default-warehouse`,
    {
      method: "PATCH",
      body: JSON.stringify({ warehouseId }),
    },
  );
}

export function applyDoPreparationWarehouse(preparationId) {
  return apiRequest(
    `/api/do-preparations/${encodeURIComponent(preparationId)}/apply-warehouse-to-items`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export function applyDepotWarehouseToDoPreparations(fleetOrderId) {
  return apiRequest(
    `/api/do-preparations/${encodeURIComponent(fleetOrderId)}/apply-depot-warehouse`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export function updateDoPreparationItemWarehouse(itemId, warehouseId) {
  return apiRequest(
    `/api/do-preparations/items/${encodeURIComponent(itemId)}/warehouse`,
    {
      method: "PATCH",
      body: JSON.stringify({ warehouseId }),
    },
  );
}

function getFleetMaster(path) {
  return apiRequest(`/api/master-data/${path}`);
}

function createFleetMaster(path, payload) {
  return apiRequest(`/api/master-data/${path}`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

function updateFleetMaster(path, id, payload) {
  return apiRequest(
    `/api/master-data/${path}/${encodeURIComponent(id)}`,
    { method: "PATCH", body: JSON.stringify(payload) },
  );
}

function deleteFleetMaster(path, id) {
  return apiRequest(
    `/api/master-data/${path}/${encodeURIComponent(id)}`,
    { method: "DELETE" },
  );
}

export const getDrivers = () => getFleetMaster("drivers");
export const createDriver = (payload) => createFleetMaster("drivers", payload);
export const updateDriver = (id, payload) =>
  updateFleetMaster("drivers", id, payload);
export const deleteDriver = (id) => deleteFleetMaster("drivers", id);

export const getCoDrivers = () => getFleetMaster("co-drivers");
export const createCoDriver = (payload) =>
  createFleetMaster("co-drivers", payload);
export const updateCoDriver = (id, payload) =>
  updateFleetMaster("co-drivers", id, payload);
export const deleteCoDriver = (id) => deleteFleetMaster("co-drivers", id);

export const getVehicles = () => getFleetMaster("vehicles");
export const createVehicle = (payload) =>
  createFleetMaster("vehicles", payload);
export const updateVehicle = (id, payload) =>
  updateFleetMaster("vehicles", id, payload);
export const deleteVehicle = (id) => deleteFleetMaster("vehicles", id);

export const getFoRoutes = () => getFleetMaster("fo-routes");
export const createFoRoute = (payload) =>
  createFleetMaster("fo-routes", payload);
export const updateFoRoute = (id, payload) =>
  updateFleetMaster("fo-routes", id, payload);
export const deleteFoRoute = (id) => deleteFleetMaster("fo-routes", id);

export function getDriverAccessAccounts() {
  return apiRequest("/api/driver/admin/accounts");
}

export function saveDriverAccessAccount(principalType, principalId, payload) {
  return apiRequest(
    `/api/driver/admin/accounts/${encodeURIComponent(principalType)}/${encodeURIComponent(principalId)}`,
    {
      method: "PUT",
      body: JSON.stringify(payload),
    },
  );
}

export async function loginDriver(payload) {
  const result = await apiRequest("/api/driver/auth/login", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  if (result?.user) {
    await putDriverCache("driver:profile", { user: result.user });
  }
  return result;
}

export function logoutDriver() {
  return apiRequest("/api/driver/auth/logout", {
    method: "POST",
    body: JSON.stringify({}),
  }).finally(() => deleteDriverCache("driver:profile"));
}

export async function getDriverProfile() {
  try {
    return await driverCachedGet("/api/driver/me", "driver:profile");
  } catch (error) {
    if (error?.status === 401) {
      await deleteDriverCache("driver:profile");
    }
    throw error;
  }
}

export function getDriverTasks() {
  return driverCachedGet("/api/driver/tasks", "driver:tasks");
}

function driverDeliveryReportStateKey(id) {
  return `driver:delivery-report-state:${id}`;
}

function localReportStages(localState) {
  if (!localState) return {};
  if (localState.stages && typeof localState.stages === "object") return localState.stages;
  if (localState.stage) return { [localState.stage]: localState };
  return {};
}

function mergeDriverDeliveryReportLocalState(payload, localState) {
  if (!payload?.deliveryReporting?.available || !localState) return payload;
  if (Number(localState.operationalDay || 0) !== Number(payload.deliveryReporting.currentDay || 0)) return payload;

  let deliveryReporting = { ...payload.deliveryReporting };
  const stages = localReportStages(localState);
  for (const [stage, stageState] of Object.entries(stages)) {
    const stageKey = { MORNING: "morning", END_OF_DAY: "endOfDay", FINAL_TRIP: "finalTrip" }[stage];
    if (!stageKey || deliveryReporting?.[stageKey]?.satisfied) continue;
    deliveryReporting = {
      ...deliveryReporting,
      morningRequired: stageKey === "morning" ? false : deliveryReporting.morningRequired,
      reporter: deliveryReporting.reporter || localState.reporter || stageState.reporter || null,
      [stageKey]: {
        satisfied: true,
        stale: false,
        overridden: Boolean(stageState.overridden),
        pendingSync: true,
        report: stageState.report || null,
      },
      morningTargetStopKeys: stageKey === "morning"
        ? (stageState.report?.targetStopKeys || deliveryReporting.morningTargetStopKeys || [])
        : deliveryReporting.morningTargetStopKeys,
    };
  }
  const morningSatisfiedLocally = Boolean(stages.MORNING)
    && !payload.deliveryReporting?.morning?.satisfied;
  const capabilities = morningSatisfiedLocally
    ? {
        ...payload.capabilities,
        canExecuteStops: Boolean(payload.capabilities?.canExecuteStopsWithoutReport),
      }
    : payload.capabilities;
  return { ...payload, deliveryReporting, capabilities };
}

async function saveLocalDriverDeliveryReportStage(id, payload, stageState) {
  const cacheKey = driverDeliveryReportStateKey(id);
  const current = await getDriverCache(cacheKey);
  const operationalDay = Number(payload.operationalDay || 1);
  const sameDay = Number(current?.operationalDay || 0) === operationalDay;
  const stages = sameDay ? localReportStages(current) : {};
  await putDriverCache(cacheKey, {
    operationalDay,
    reporter: current?.reporter || {
      type: payload.reporterType,
      name: payload.reporterName,
    },
    stages: {
      ...stages,
      [payload.stage]: stageState,
    },
    updatedAt: new Date().toISOString(),
  });
}

async function clearLocalDriverDeliveryReportStage(id, stage) {
  const cacheKey = driverDeliveryReportStateKey(id);
  const current = await getDriverCache(cacheKey);
  if (!current) return;
  const stages = { ...localReportStages(current) };
  delete stages[stage];
  if (Object.keys(stages).length === 0) {
    await deleteDriverCache(cacheKey);
    return;
  }
  await putDriverCache(cacheKey, { ...current, stages, updatedAt: new Date().toISOString() });
}

async function invalidateLocalDriverClosingReports(id) {
  await clearLocalDriverDeliveryReportStage(id, "END_OF_DAY");
  await clearLocalDriverDeliveryReportStage(id, "FINAL_TRIP");
}

function mergeDriverRouteDraftIntoDetail(payload, routeDraft) {
  if (!payload?.stops?.length || !routeDraft?.stopKeys?.length || routeDraft.syncStatus === "CONFLICT") {
    return { ...payload, routeDraft: routeDraft || null };
  }
  const stopByKey = new Map(payload.stops.map((stop) => [String(stop.key), stop]));
  if (routeDraft.stopKeys.some((key) => !stopByKey.has(String(key)))) {
    return { ...payload, routeDraft: { ...routeDraft, syncStatus: "CONFLICT" } };
  }
  const editableKeySet = new Set(routeDraft.stopKeys.map(String));
  const prefix = payload.stops.filter((stop) => {
    const status = String(stop.status || "PENDING").toUpperCase();
    return status === "EN_ROUTE" || Boolean(stop.actualSequence || stop.startedAt || stop.completedAt);
  });
  const prefixKeys = new Set(prefix.map((stop) => String(stop.key)));
  const editable = routeDraft.stopKeys.map((key) => stopByKey.get(String(key))).filter(Boolean);
  const suffix = payload.stops.filter((stop) => !prefixKeys.has(String(stop.key)) && !editableKeySet.has(String(stop.key)));
  return {
    ...payload,
    stops: [...prefix, ...editable, ...suffix].map((stop, index) => ({ ...stop, sequence: index + 1 })),
    execution: {
      ...payload.execution,
      routeVersion: Math.max(
        Number(payload.execution?.routeVersion || 1),
        Number(routeDraft.expectedRouteVersion || 1) + 1,
      ),
      routeSyncPending: true,
    },
    routeDraft,
  };
}

export async function getDriverTaskDetail(id) {
  const [payload, routeDraft, localReportState] = await Promise.all([
    driverCachedGet(`/api/driver/tasks/${encodeURIComponent(id)}`, `driver:task:${id}`),
    getDriverRouteDraft(id),
    getDriverCache(driverDeliveryReportStateKey(id)),
  ]);
  return mergeDriverRouteDraftIntoDetail(
    mergeDriverDeliveryReportLocalState(payload, localReportState),
    routeDraft,
  );
}

export async function getDriverTaskMap(id) {
  const [payload, localReportState] = await Promise.all([
    driverCachedGet(`/api/driver/tasks/${encodeURIComponent(id)}/map`, `driver:map:${id}`),
    getDriverCache(driverDeliveryReportStateKey(id)),
  ]);
  return mergeDriverDeliveryReportLocalState(payload, localReportState);
}

export async function getDriverRouteWorkspace(id) {
  const [payload, routeDraft] = await Promise.all([
    driverCachedGet(
      `/api/driver/tasks/${encodeURIComponent(id)}/route-workspace`,
      `driver:route-workspace:${id}`,
    ),
    getDriverRouteDraft(id),
  ]);
  return { ...payload, routeDraft };
}

function requireDriverRouteOnline() {
  if (navigator.onLine) return;
  const error = new Error("Pengaturan rute membutuhkan koneksi internet.");
  error.code = "DRIVER_ROUTE_ONLINE_REQUIRED";
  throw error;
}

export function optimizeDriverTaskRoute(id) {
  requireDriverRouteOnline();
  return apiRequest(`/api/driver/tasks/${encodeURIComponent(id)}/route/optimize`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function previewDriverTaskRoute(id, orderedStopKeys) {
  requireDriverRouteOnline();
  return apiRequest(`/api/driver/tasks/${encodeURIComponent(id)}/route/preview`, {
    method: "POST",
    body: JSON.stringify({ orderedStopKeys }),
  });
}

export async function saveDriverTaskRoute(id, payload) {
  const idempotencyKey = payload?.idempotencyKey || driverActionKey();
  const body = {
    ...payload,
    fleetOrderId: id,
    idempotencyKey,
    clientSavedAt: new Date().toISOString(),
  };
  const result = await driverWrite(`/api/driver/tasks/${encodeURIComponent(id)}/route`, {
    method: "PUT",
    payload: body,
    entityType: "FLEET_ORDER",
    action: "REORDER_STOPS",
    dedupeKey: `driver-route:${id}`,
  });

  if (result?.queued) {
    const routeDraft = await saveDriverRouteDraft(id, {
      ...body,
      stopKeys: body.stopKeys,
      syncStatus: "PENDING",
      queuedAt: new Date().toISOString(),
    });
    return { ...result, routeDraft };
  }

  await clearDriverRouteDraft(id, { idempotencyKey });
  return result;
}

export async function discardDriverTaskRouteDraft(id) {
  await discardPendingDriverRoute(id);
  return getDriverRouteWorkspace(id);
}

function driverActionKey() {
  return globalThis.crypto?.randomUUID?.() || `driver-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

async function driverCachedGet(path, cacheKey) {
  try {
    const payload = await apiRequest(path, {
      cache: "no-store",
      timeoutMs: DRIVER_CACHED_GET_TIMEOUT_MS,
      maxAttempts: 1,
    });
    await putDriverCache(cacheKey, payload);
    return payload;
  } catch (error) {
    const cached = await getDriverCache(cacheKey);
    if (cached && (!navigator.onLine || !error.status || error.status >= 500)) {
      return { ...cached, offlineCache: true };
    }
    throw error;
  }
}

async function driverWrite(path, { method = "POST", payload = {}, entityType, action, dedupeKey = null }) {
  const idempotencyKey = payload.idempotencyKey || driverActionKey();
  const clientSavedAt = payload.clientSavedAt || new Date().toISOString();
  const onlineBody = {
    ...payload,
    idempotencyKey,
    clientSavedAt,
    syncMode: payload.syncMode || "ONLINE",
  };
  const queueBody = {
    ...onlineBody,
    syncMode: "OFFLINE_QUEUE",
    queuedAt: new Date().toISOString(),
  };
  const queue = () => queueDriverAction({
    id: idempotencyKey,
    entityType,
    action,
    path,
    method,
    payload: queueBody,
    dedupeKey,
  }).then(() => ({ queued: true, offline: true, idempotencyKey }));
  const mediaResolution = await resolveDriverMediaReferences(onlineBody);
  if (!navigator.onLine || mediaResolution.hasUnsynced) return queue();
  try {
    return await apiRequest(path, { method, body: JSON.stringify(mediaResolution.payload) });
  } catch (error) {
    if (!error.status || error.status === 401 || [408, 425, 429].includes(error.status) || error.status >= 500) return queue();
    throw error;
  }
}

export function reorderDriverTaskStops(id, stopKeys) {
  return driverWrite(`/api/driver/tasks/${encodeURIComponent(id)}/route`, {
    method: "PUT", payload: { stopKeys }, entityType: "FLEET_ORDER", action: "REORDER_STOPS",
  });
}

export function saveDriverLoadingChecks(id, input) {
  const payload = Array.isArray(input) ? { checks: input } : { ...(input || {}) };
  return driverWrite(`/api/driver/tasks/${encodeURIComponent(id)}/loading`, {
    method: "PUT",
    payload: { ...payload, fleetOrderId: id },
    entityType: "FLEET_ORDER",
    action: "SAVE_LOADING",
    dedupeKey: `driver-loading:${id}`,
  });
}

export function confirmDriverLoading(id, payload = {}) {
  return driverWrite(`/api/driver/tasks/${encodeURIComponent(id)}/loading/confirm`, {
    payload: { ...payload, fleetOrderId: id }, entityType: "FLEET_ORDER", action: "CONFIRM_LOADING",
  });
}

export function acknowledgeDriverFleetOrderAmendment(id, amendmentId) {
  return driverWrite(
    `/api/driver/tasks/${encodeURIComponent(id)}/amendments/${encodeURIComponent(amendmentId)}/acknowledge`,
    {
      payload: {},
      entityType: "FLEET_ORDER",
      action: "ACKNOWLEDGE_AMENDMENT",
    },
  );
}

export function startDriverTrip(id, payload = {}) {
  return driverWrite(`/api/driver/tasks/${encodeURIComponent(id)}/start`, {
    payload, entityType: "TRIP_SESSION", action: "START_TRIP",
  });
}

export async function startDriverStop(id, payload) {
  const result = await driverWrite(`/api/driver/tasks/${encodeURIComponent(id)}/stops/start`, {
    payload, entityType: "STOP", action: "START_STOP",
  });
  await invalidateLocalDriverClosingReports(id);
  return result;
}

export function makeDriverStopNext(id, payload) {
  return driverWrite(`/api/driver/tasks/${encodeURIComponent(id)}/stops/next`, {
    payload, entityType: "STOP", action: "MAKE_STOP_NEXT",
  });
}

export async function completeDriverStop(id, payload) {
  const result = await driverWrite(`/api/driver/tasks/${encodeURIComponent(id)}/stops/complete`, {
    payload, entityType: "STOP", action: "COMPLETE_STOP",
  });
  await invalidateLocalDriverClosingReports(id);
  return result;
}

export async function failDriverStop(id, payload) {
  const result = await driverWrite(`/api/driver/tasks/${encodeURIComponent(id)}/stops/fail`, {
    payload, entityType: "STOP", action: "FAIL_STOP",
  });
  await invalidateLocalDriverClosingReports(id);
  return result;
}

export async function confirmDriverDeliveryReport(id, payload) {
  const result = await driverWrite(`/api/driver/tasks/${encodeURIComponent(id)}/delivery-reports/confirm`, {
    payload: { ...payload, fleetOrderId: id },
    entityType: "DELIVERY_REPORT",
    action: "CONFIRM_DELIVERY_REPORT",
  });
  if (result?.queued) {
    await saveLocalDriverDeliveryReportStage(id, payload, {
      overridden: false,
      reporter: { type: payload.reporterType, name: payload.reporterName },
      report: {
        type: payload.reportType || payload.stage,
        reportText: payload.reportText,
        targetStopKeys: payload.targetStopKeys || [],
        shareMethod: payload.shareMethod,
        confirmedAt: payload.confirmedAt || new Date().toISOString(),
      },
    });
  } else {
    await clearLocalDriverDeliveryReportStage(id, payload.stage);
  }
  return result;
}

export async function overrideDriverDeliveryReport(id, payload) {
  const result = await driverWrite(`/api/driver/tasks/${encodeURIComponent(id)}/delivery-reports/override`, {
    payload: { ...payload, fleetOrderId: id },
    entityType: "DELIVERY_REPORT",
    action: "OVERRIDE_DELIVERY_REPORT",
  });
  if (result?.queued) {
    await saveLocalDriverDeliveryReportStage(id, payload, {
      overridden: true,
      reporter: { type: payload.reporterType, name: payload.reporterName },
      report: {
        type: "OVERRIDE",
        targetStopKeys: [],
        confirmedAt: new Date().toISOString(),
        overrideReason: payload.reason,
      },
    });
  } else {
    await clearLocalDriverDeliveryReportStage(id, payload.stage);
  }
  return result;
}

export function getDriverInspectionTemplates(type) {
  return driverCachedGet(withQuery("/api/driver/inspection-templates", { type }), `driver:inspection-templates:${type || "all"}`);
}

export function submitDriverInspection(id, payload) {
  return driverWrite(`/api/driver/tasks/${encodeURIComponent(id)}/inspections`, {
    payload, entityType: "VEHICLE_INSPECTION", action: `SUBMIT_${payload.inspectionType}`,
  });
}

export function closeDriverTripDay(id, payload) {
  return driverWrite(`/api/driver/tasks/${encodeURIComponent(id)}/trip-sessions/close-day`, {
    payload, entityType: "TRIP_SESSION", action: "CLOSE_DAY",
  });
}

export function continueDriverTrip(id, payload) {
  return driverWrite(`/api/driver/tasks/${encodeURIComponent(id)}/trip-sessions/continue`, {
    payload, entityType: "TRIP_SESSION", action: "CONTINUE_DAY",
  });
}

export function markDriverReturned(id, payload) {
  return driverWrite(`/api/driver/tasks/${encodeURIComponent(id)}/trip-sessions/returned`, {
    payload, entityType: "TRIP_SESSION", action: "CONFIRM_RETURN",
  });
}

export function getDriverExpenseCategories() {
  return driverCachedGet("/api/driver/expense-categories", "driver:expense-categories");
}

export function getDriverExpenses(fleetOrderId = null) {
  return driverCachedGet(withQuery("/api/driver/expenses", { fleetOrderId }), `driver:expenses:${fleetOrderId || "all"}`);
}

export function createDriverExpense(payload) {
  return driverWrite("/api/driver/expenses", { payload, entityType: "EXPENSE", action: "CREATE_EXPENSE" });
}

export function getDriverExpenseSummary(id) {
  return driverCachedGet(`/api/driver/tasks/${encodeURIComponent(id)}/expenses/summary`, `driver:expense-summary:${id}`);
}

export function confirmDriverExpenses(id, payload = {}) {
  return driverWrite(`/api/driver/tasks/${encodeURIComponent(id)}/expenses/confirm`, {
    payload, entityType: "EXPENSE_REVIEW", action: "CONFIRM_EXPENSES",
  });
}

export function getDriverIncidents(fleetOrderId = null) {
  return driverCachedGet(withQuery("/api/driver/incidents", { fleetOrderId }), `driver:incidents:${fleetOrderId || "all"}`);
}

export function createDriverIncident(payload) {
  return driverWrite("/api/driver/incidents", { payload, entityType: "INCIDENT", action: "REPORT_INCIDENT" });
}

export function getDriverVehicleWashes() {
  return driverCachedGet("/api/driver/vehicle-washes", "driver:vehicle-washes");
}

export function getDriverActiveVehicles() {
  return driverCachedGet("/api/driver/vehicles", "driver:vehicles");
}

export function createDriverVehicleWash(payload) {
  return driverWrite("/api/driver/vehicle-washes", { payload, entityType: "VEHICLE_WASH", action: "CREATE_WASH_LOG" });
}

export function getDriverNotifications() {
  return driverCachedGet("/api/driver/notifications", "driver:notifications");
}

export function markDriverNotificationRead(id) {
  return apiRequest(`/api/driver/notifications/${encodeURIComponent(id)}/read`, { method: "POST", body: JSON.stringify({}) });
}

export function registerDriverMedia(payload) {
  return apiRequest("/api/driver/media", { method: "POST", body: JSON.stringify(payload) });
}

export function completeDriverFleetOrder(id, payload) {
  return driverWrite(`/api/driver/tasks/${encodeURIComponent(id)}/complete`, {
    payload, entityType: "FLEET_ORDER", action: "COMPLETE_FLEET_ORDER",
  });
}

export function getDriverAdminMonitoring(fleetOrderId = null) {
  return apiRequest(withQuery("/api/driver/admin/monitoring", { fleetOrderId }));
}

export function acknowledgeDriverIncident(id, payload) {
  return apiRequest(`/api/driver/admin/incidents/${encodeURIComponent(id)}`, {
    method: "PATCH", body: JSON.stringify(payload),
  });
}
